import { useState, useMemo, lazy, Suspense } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Search, Maximize2, Check, X, MapPin, Ruler, DollarSign, Plus, GitCompare, Rotate3d } from "lucide-react";
import { toast } from "sonner";
import { PROPIEDADES, INTERESADOS, AGENTES, agentePorId, propiedadPorId } from "../data/mockData";
import { RECORRIDOS, tieneRecorrido } from "../data/recorridos";

const Visor360 = lazy(() => import("../components/Visor360"));
import { pesos, fechaCorta } from "../lib/format";

export default function Propiedades() {
  const [tab, setTab] = useState("propiedades");
  return (
    <div>
      <PageHeader
        titulo="Propiedades e interesados"
        descripcion="Ficha clara de cada propiedad, comparador lado a lado y seguimiento de interesados para que el comprador no se quede con dudas."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-md mb-5">
          <TabsTrigger value="propiedades" data-testid="props-tab-propiedades" className="rounded-md">Propiedades</TabsTrigger>
          <TabsTrigger value="interesados" data-testid="props-tab-interesados" className="rounded-md">Interesados</TabsTrigger>
        </TabsList>
        <TabsContent value="propiedades"><Catalogo /></TabsContent>
        <TabsContent value="interesados"><Leads /></TabsContent>
      </Tabs>
    </div>
  );
}

function Catalogo() {
  const [q, setQ] = useState("");
  const [barrio, setBarrio] = useState("todos");
  const [op, setOp] = useState("todos");
  const [sel, setSel] = useState(null);
  const [comparar, setComparar] = useState([]);
  const [openCompare, setOpenCompare] = useState(false);
  const [tour, setTour] = useState(null);

  const filtradas = useMemo(() => {
    const t = q.toLowerCase();
    return PROPIEDADES.filter(
      (p) =>
        (p.titulo.toLowerCase().includes(t) || p.direccion.toLowerCase().includes(t)) &&
        (barrio === "todos" || p.barrio === barrio) &&
        (op === "todos" || p.operacion === op)
    );
  }, [q, barrio, op]);

  const toggleCompare = (p) => {
    setComparar((c) => {
      if (c.find((x) => x.id === p.id)) return c.filter((x) => x.id !== p.id);
      if (c.length >= 3) { toast.error("Podés comparar hasta 3 propiedades."); return c; }
      return [...c, p];
    });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar propiedad o dirección…" className="pl-9 rounded-md" data-testid="properties-search-input" />
        </div>
        <Select value={barrio} onValueChange={setBarrio}>
          <SelectTrigger className="w-full sm:w-[150px] rounded-md" data-testid="properties-barrio-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los barrios</SelectItem>
            <SelectItem value="Palermo">Palermo</SelectItem>
            <SelectItem value="Caballito">Caballito</SelectItem>
            <SelectItem value="Villa Crespo">Villa Crespo</SelectItem>
            <SelectItem value="Belgrano">Belgrano</SelectItem>
          </SelectContent>
        </Select>
        <Select value={op} onValueChange={setOp}>
          <SelectTrigger className="w-full sm:w-[140px] rounded-md"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Alquiler y venta</SelectItem>
            <SelectItem value="Alquiler">Alquiler</SelectItem>
            <SelectItem value="Venta">Venta</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {comparar.length > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-md border border-brand bg-brand-light px-4 py-2">
          <GitCompare className="h-4 w-4 text-brand" />
          <span className="text-sm text-slate-700">{comparar.length} seleccionada(s) para comparar</span>
          <Button size="sm" className="btn-brand rounded-md ml-auto" data-testid="property-compare-button" disabled={comparar.length < 2} onClick={() => setOpenCompare(true)}>
            Comparar lado a lado
          </Button>
          <Button size="sm" variant="ghost" className="rounded-md" onClick={() => setComparar([])}>Limpiar</Button>
        </div>
      )}

      <Card className="mb-5 p-5 rounded-lg border-slate-200 shadow-sm" data-testid="tour360-section">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-brand">Recorridos virtuales 360°</p>
            <h3 className="text-lg font-bold text-slate-900 mt-1">Que lo recorran antes de visitarlo</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              Con fotos 360° de cada ambiente armamos un recorrido para que el interesado camine la propiedad desde el celular. Lo sumamos a la ficha y a tus publicaciones.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {["Menos visitas en vano", "Llegás a interesados que están lejos", "Tu publicación se destaca"].map((b) => (
                <span key={b} className="rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">{b}</span>
              ))}
            </div>
          </div>
          <Button
            className="btn-brand rounded-md shrink-0"
            data-testid="tour-example-button"
            onClick={() => {
              const p = propiedadPorId("prop-1");
              setTour({ propId: p.id, titulo: p.titulo });
            }}
          >
            <Rotate3d className="mr-1.5 h-4 w-4" /> Ver un ejemplo
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="properties-grid">
        {filtradas.map((p) => {
          const enComp = comparar.find((x) => x.id === p.id);
          return (
            <Card key={p.id} data-testid={`property-card-${p.id}`} className="rounded-lg border-slate-200 shadow-sm overflow-hidden group">
              <div className="relative h-40 bg-slate-100 overflow-hidden">
                <img src={p.imagen} alt={p.titulo} className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]" />
                <div className="absolute top-2 left-2 flex gap-1">
                  <Badge variant="outline" className="rounded-md bg-white/90 text-slate-700 text-[11px]">{p.operacion}</Badge>
                  <StatusBadge estado={p.estadoOcupacion} className="text-[11px]" />
                </div>
                {tieneRecorrido(p.id) && (
                  <Badge
                    data-testid={`property-360-badge-${p.id}`}
                    variant="outline"
                    className="absolute top-2 right-2 rounded-md bg-black/70 text-white border-0 text-[11px] font-semibold"
                  >
                    360°
                  </Badge>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm font-semibold text-slate-900 truncate">{p.titulo}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {p.direccion}, {p.barrio}</p>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-lg font-bold text-slate-900">{p.operacion === "Venta" ? "USD " + p.precio.toLocaleString("es-AR") : pesos(p.precio)}</p>
                    <p className="text-[11px] text-slate-400">{p.operacion === "Alquiler" ? `+ ${pesos(p.expensas)} expensas` : `${pesos(p.expensas)} expensas`}</p>
                  </div>
                  <p className="text-xs text-slate-500">{p.m2} m² · {p.ambientes} amb</p>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-md flex-1" data-testid={`property-view-${p.id}`} onClick={() => setSel(p)}>
                    <Maximize2 className="mr-1.5 h-3.5 w-3.5" /> Ficha
                  </Button>
                  <Button size="sm" variant={enComp ? "default" : "outline"} className={`rounded-md ${enComp ? "btn-brand" : ""}`} data-testid={`property-compare-toggle-${p.id}`} onClick={() => toggleCompare(p)}>
                    {enComp ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Ficha */}
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {sel && (
            <>
              <SheetHeader><SheetTitle>{sel.titulo}</SheetTitle></SheetHeader>
              <img src={sel.imagen} alt={sel.titulo} className="mt-4 h-52 w-full rounded-md object-cover" />
              {tieneRecorrido(sel.id) && (
                <Button
                  className="btn-brand rounded-md mt-3 w-full"
                  data-testid={`property-tour-button-${sel.id}`}
                  onClick={() => {
                    setSel(null);
                    setTour({ propId: sel.id, titulo: sel.titulo });
                  }}
                >
                  <Rotate3d className="mr-1.5 h-4 w-4" /> Recorrer en 360°
                </Button>
              )}
              <div className="mt-4 flex items-center gap-2">
                <Badge variant="outline" className="rounded-md">{sel.operacion}</Badge>
                <StatusBadge estado={sel.estadoOcupacion} />
                <Badge variant="outline" className="rounded-md">{sel.tipo}</Badge>
              </div>
              <p className="mt-4 text-2xl font-bold text-slate-900">{sel.operacion === "Venta" ? "USD " + sel.precio.toLocaleString("es-AR") : pesos(sel.precio)}</p>
              <p className="text-sm text-slate-500">{pesos(sel.expensas)} de expensas · {sel.barrio}</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Spec l="Superficie" v={`${sel.m2} m² (${sel.m2Cubiertos} cub.)`} />
                <Spec l="Precio por m²" v={sel.operacion === "Venta" ? `USD ${Math.round(sel.precio / sel.m2).toLocaleString("es-AR")}` : pesos(Math.round(sel.precio / sel.m2))} />
                <Spec l="Ambientes" v={`${sel.ambientes} amb · ${sel.dormitorios} dorm`} />
                <Spec l="Baños" v={sel.banos} />
                <Spec l="Antigüedad" v={`${sel.antiguedad} años`} />
                <Spec l="Mascotas" v={sel.mascotas ? "Sí" : "No"} />
                <Spec l="Cochera" v={sel.cochera ? "Sí" : "No"} />
                <Spec l="Agente" v={agentePorId(sel.agenteId)?.nombre} />
              </div>
              <p className="mt-4 text-[11px] uppercase tracking-wide font-semibold text-slate-400">Amenities</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {sel.amenities.length ? sel.amenities.map((a) => <Badge key={a} variant="outline" className="rounded-md">{a}</Badge>) : <span className="text-sm text-slate-400">Sin amenities</span>}
              </div>
              <p className="mt-4 text-[11px] uppercase tracking-wide font-semibold text-slate-400">Requisitos</p>
              <p className="text-sm text-slate-600">{sel.requisitos}</p>
              <p className="mt-4 text-sm text-slate-600">{sel.descripcion}</p>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Comparador */}
      <Dialog open={openCompare} onOpenChange={setOpenCompare}>
        <DialogContent className="max-w-3xl" data-testid="property-compare-modal">
          <DialogHeader><DialogTitle>Comparador de propiedades</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                <CompareRow label="" values={comparar.map((p) => <img key={p.id} src={p.imagen} alt="" className="h-20 w-full rounded-md object-cover" />)} />
                <CompareRow label="Dirección" values={comparar.map((p) => p.direccion)} bold />
                <CompareRow label="Barrio" values={comparar.map((p) => p.barrio)} />
                <CompareRow label="Precio" values={comparar.map((p) => p.operacion === "Venta" ? "USD " + p.precio.toLocaleString("es-AR") : pesos(p.precio))} bold />
                <CompareRow label="Precio por m²" values={comparar.map((p) => p.operacion === "Venta" ? "USD " + Math.round(p.precio / p.m2).toLocaleString("es-AR") : pesos(Math.round(p.precio / p.m2)))} highlight />
                <CompareRow label="Expensas" values={comparar.map((p) => pesos(p.expensas))} />
                <CompareRow label="Superficie" values={comparar.map((p) => `${p.m2} m²`)} />
                <CompareRow label="Ambientes" values={comparar.map((p) => `${p.ambientes} amb`)} />
                <CompareRow label="Antigüedad" values={comparar.map((p) => `${p.antiguedad} años`)} />
                <CompareRow label="Mascotas" values={comparar.map((p) => (p.mascotas ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-red-500" />))} />
                <CompareRow label="Cochera" values={comparar.map((p) => (p.cochera ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-red-500" />))} />
                <CompareRow label="Amenities" values={comparar.map((p) => p.amenities.join(", ") || "—")} />
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {tour && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-[90] bg-black flex items-center justify-center text-white text-sm">
              Cargando recorrido…
            </div>
          }
        >
          <Visor360
            tour={RECORRIDOS[tour.propId]}
            titulo={tour.titulo}
            onClose={() => setTour(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

function CompareRow({ label, values, bold, highlight }) {
  return (
    <tr className="border-b border-slate-100">
      <td className="py-2 pr-3 text-xs font-semibold text-slate-400 uppercase tracking-wide w-32 align-middle">{label}</td>
      {values.map((v, i) => (
        <td key={i} className={`py-2 px-3 align-middle ${bold ? "font-semibold text-slate-900" : "text-slate-600"} ${highlight ? "text-brand font-semibold" : ""}`}>{v}</td>
      ))}
    </tr>
  );
}

function Spec({ l, v }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <p className="text-[11px] text-slate-400">{l}</p>
      <p className="text-slate-800 font-medium">{v}</p>
    </div>
  );
}

function Leads() {
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("todos");
  const [sel, setSel] = useState(null);
  const [asign, setAsign] = useState({});
  const asignar = (leadId, agenteId) => {
    setAsign((prev) => ({ ...prev, [leadId]: agenteId }));
    const ag = agentePorId(agenteId);
    toast.success(`Lead asignado a ${ag?.nombre}.`);
  };
  const filtrados = INTERESADOS.filter(
    (l) => l.nombre.toLowerCase().includes(q.toLowerCase()) && (estado === "todos" || l.estado === estado)
  );
  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar interesado…" className="pl-9 rounded-md" data-testid="leads-search-input" />
        </div>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="w-full sm:w-[190px] rounded-md"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="Nuevo">Nuevo</SelectItem>
            <SelectItem value="Contactado">Contactado</SelectItem>
            <SelectItem value="Visita agendada">Visita agendada</SelectItem>
            <SelectItem value="En negociación">En negociación</SelectItem>
            <SelectItem value="Cerrado">Cerrado</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leads-tracking-timeline">
        {filtrados.map((l) => (
          <Card key={l.id} data-testid={`lead-card-${l.id}`} className="p-4 rounded-lg border-slate-200 shadow-sm cursor-pointer hover:border-slate-300" onClick={() => setSel(l)}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-slate-900">{l.nombre}</p>
              <StatusBadge estado={l.estado} />
            </div>
            <p className="text-xs text-slate-500 truncate">{l.propiedadTitulo}</p>
            <p className="text-xs text-slate-400 mt-2">Origen: {l.origen} · {fechaCorta(l.ultimaInteraccion)}</p>
            <div className="mt-3 rounded-md bg-slate-50 px-3 py-2">
              <p className="text-[11px] text-slate-400">Próximo paso</p>
              <p className="text-sm text-slate-700">{l.proximoPaso}</p>
            </div>
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <Label className="text-[11px] text-slate-400">Vendedor asignado</Label>
              <Select value={asign[l.id] ?? l.agenteId} onValueChange={(v) => asignar(l.id, v)}>
                <SelectTrigger data-testid={`lead-assign-${l.id}`} className="h-8 rounded-md mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AGENTES.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>
        ))}
      </div>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {sel && (
            <>
              <SheetHeader><SheetTitle>{sel.nombre}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-2 text-sm">
                <p className="text-slate-600">{sel.telefono} · {sel.email}</p>
                <p className="text-slate-600">Consultó por: <span className="font-medium text-slate-800">{sel.propiedadTitulo}</span></p>
                <StatusBadge estado={sel.estado} />
              </div>
              <div className="mt-4">
                <Label className="text-xs text-slate-500">Vendedor asignado</Label>
                <Select value={asign[sel.id] ?? sel.agenteId} onValueChange={(v) => asignar(sel.id, v)}>
                  <SelectTrigger data-testid="lead-detail-assign" className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AGENTES.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="mt-5 text-[11px] uppercase tracking-wide font-semibold text-slate-400">Historial de interacción</p>
              <div className="mt-2 space-y-3">
                {sel.historial.map((h, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="h-2 w-2 rounded-full bg-brand mt-1.5" />
                      {i < sel.historial.length - 1 && <span className="w-px flex-1 bg-slate-200" />}
                    </div>
                    <div className="pb-1">
                      <p className="text-xs text-slate-400">{fechaCorta(h.fecha)}</p>
                      <p className="text-sm text-slate-700">{h.evento}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button className="btn-brand rounded-md w-full mt-5" onClick={() => toast.success("Interesado contactado por WhatsApp (demo).")}>
                Contactar interesado
              </Button>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
